import {
  Box,
  Button,
  type ButtonProps,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
} from "@chakra-ui/react"
import React from "react"
import { FiChevronDown } from "react-icons/fi"

type AppSelectProps = Omit<ButtonProps, "onChange"> & {
  children?: React.ReactNode
  value?: string | number
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void
  placeholder?: string
  name?: string
}

type ParsedOption = {
  value: string
  label: React.ReactNode
  disabled: boolean
  groupLabel?: string
}

function collectOptions(
  children: React.ReactNode,
  groupLabel?: string,
): ParsedOption[] {
  const result: ParsedOption[] = []

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return

    if (child.type === React.Fragment) {
      result.push(...collectOptions(child.props.children, groupLabel))
      return
    }

    if (typeof child.type !== "string") return
    const elementType = child.type.toLowerCase()

    if (elementType === "option") {
      const optionValue = String(child.props.value ?? "")
      result.push({
        value: optionValue,
        label: child.props.children,
        disabled: Boolean(child.props.disabled),
        groupLabel,
      })
      return
    }

    if (elementType === "optgroup") {
      const nestedGroupLabel = String(child.props.label ?? "")
      result.push(...collectOptions(child.props.children, nestedGroupLabel))
    }
  })

  return result
}

export default function AppSelect({
  children,
  value,
  onChange,
  placeholder,
  name,
  isDisabled,
  minW,
  w,
  width,
  variant = "wmsDropdown",
  size = "md",
  ...buttonProps
}: AppSelectProps) {
  const options = React.useMemo(() => collectOptions(children), [children])
  const currentValue = value == null ? "" : String(value)
  const selectedOption = options.find((option) => option.value === currentValue)
  const emptyOption = options.find((option) => option.value === "")
  const displayLabel =
    selectedOption?.label ?? placeholder ?? emptyOption?.label
  const grouped = React.useMemo(() => {
    const groupOrder: string[] = []
    const groupItems = new Map<string, ParsedOption[]>()
    const ungrouped: ParsedOption[] = []

    for (const option of options) {
      if (!option.groupLabel) {
        ungrouped.push(option)
        continue
      }
      if (!groupItems.has(option.groupLabel)) {
        groupItems.set(option.groupLabel, [])
        groupOrder.push(option.groupLabel)
      }
      groupItems.get(option.groupLabel)?.push(option)
    }

    return { groupItems, groupOrder, ungrouped }
  }, [options])

  const handleValueChange = (nextValue: string) => {
    if (!onChange) return
    const syntheticEvent = {
      target: { value: nextValue, name },
      currentTarget: { value: nextValue, name },
    } as React.ChangeEvent<HTMLSelectElement>
    onChange(syntheticEvent)
  }

  const menuMinW = minW ?? w ?? width

  return (
    <Menu>
      <MenuButton
        as={Button}
        size={size}
        variant={variant}
        isDisabled={isDisabled}
        rightIcon={<FiChevronDown />}
        justifyContent="space-between"
        minW={minW}
        w={w}
        width={width}
        {...buttonProps}
      >
        <Box textAlign="left" noOfLines={1}>
          {displayLabel ?? "Wybierz..."}
        </Box>
      </MenuButton>
      <MenuList minW={menuMinW} maxH="300px" overflowY="auto">
        {grouped.ungrouped.map((option) => (
          <MenuItem
            key={`option-${option.value}-${String(option.label)}`}
            isDisabled={option.disabled}
            onClick={() => handleValueChange(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
        {grouped.groupOrder.map((groupLabel) => (
          <MenuGroup key={`group-${groupLabel}`} title={groupLabel}>
            {grouped.groupItems.get(groupLabel)?.map((option) => (
              <MenuItem
                key={`group-option-${groupLabel}-${option.value}-${String(option.label)}`}
                isDisabled={option.disabled}
                onClick={() => handleValueChange(option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuGroup>
        ))}
      </MenuList>
    </Menu>
  )
}
